import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, X, ZoomIn, ZoomOut, Maximize2, Pill, User2, Phone, Mail, Stethoscope, Calendar, FileText, AlertTriangle, Sparkles, RefreshCw, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "../../admin/AdminLayout";
import AdminAuthGuard from "../../admin/AdminAuthGuard";
import { api } from "../../api/client";

const REJECTION_REASONS = [
    { value: "Illegible / unclear image", code: "illegible" },
    { value: "Doctor not registered / cannot verify", code: "doctor_not_registered" },
    { value: "Prescription is expired", code: "expired" },
    { value: "Patient mismatch / wrong account", code: "patient_mismatch" },
    { value: "Suspected fraud or forgery", code: "suspected_fraud" },
    { value: "Other", code: "other" },
];

const TABS = [
    { value: "ocr", label: "OCR data" },
    { value: "copilot", label: "AI Co-pilot" },
    { value: "customer", label: "Customer" },
    { value: "linked", label: "Cart & orders" },
];

const SEVERITY_TONE = {
    severe: "border-rose-200 bg-rose-50 text-rose-800",
    moderate: "border-amber-200 bg-amber-50 text-amber-800",
    low: "border-slate-200 bg-slate-50 text-slate-700",
};

const ACTION_TONE = {
    approve: "bg-emerald-100 text-emerald-800 ring-emerald-200",
    review: "bg-amber-100 text-amber-800 ring-amber-200",
    reject: "bg-rose-100 text-rose-800 ring-rose-200",
};

const InfoRow = ({ icon: Icon, label, value }) => (
    <div className="flex items-start gap-2.5 py-1.5">
        {Icon && <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />}
        <div className="min-w-0 flex-1">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</p>
            <p className="text-sm text-slate-900">{value || <span className="text-slate-300">—</span>}</p>
        </div>
    </div>
);

const Content = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [rx, setRx] = useState(null);
    const [loading, setLoading] = useState(true);
    const [zoom, setZoom] = useState(1);
    const [tab, setTab] = useState("ocr");
    const [notes, setNotes] = useState("");
    const [rejectOpen, setRejectOpen] = useState(false);
    const [rejectReason, setRejectReason] = useState(REJECTION_REASONS[0].value);
    const [rejectNotes, setRejectNotes] = useState("");
    const [busy, setBusy] = useState(false);
    const [copilot, setCopilot] = useState(null);
    const [copilotLoading, setCopilotLoading] = useState(false);

    const load = async () => {
        try {
            setLoading(true);
            const { data } = await api.get(`/admin/prescriptions/${id}`);
            setRx(data);
            setCopilot(data?.copilot_analysis || null);
        } catch {
            toast.error("Could not load prescription");
            navigate("/admin/prescriptions");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { setNotes(""); setZoom(1); setTab("ocr"); setCopilot(null); load(); /* eslint-disable-next-line */ }, [id]);

    const runCopilot = async (force = false) => {
        if (copilotLoading) return;
        setCopilotLoading(true);
        try {
            const { data } = await api.post(`/admin/prescriptions/${id}/copilot`, null, { params: force ? { force: true } : {} });
            setCopilot(data);
            if (data.cached) toast.message("Showing cached AI analysis");
            else toast.success(`AI co-pilot done in ${data.elapsed_seconds?.toFixed?.(1) || "—"}s`);
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : d?.message || "Co-pilot unavailable");
        } finally {
            setCopilotLoading(false);
        }
    };

    const goToNextPending = async () => {
        try {
            const r = await api.get("/admin/prescriptions", { params: { status: "pending_review", page_size: 1 } });
            const next = (r.data.items || []).find((p) => p.id !== id);
            if (next) navigate(`/admin/prescriptions/${next.id}`);
            else navigate("/admin/prescriptions");
        } catch {
            navigate("/admin/prescriptions");
        }
    };

    const approve = async () => {
        if (busy) return;
        setBusy(true);
        try {
            await api.post(`/admin/prescriptions/${id}/approve`, { notes: notes || null });
            toast.success("Prescription approved");
            await goToNextPending();
        } catch (err) {
            toast.error(err?.response?.data?.detail?.message || "Could not approve");
        } finally {
            setBusy(false);
        }
    };

    const reject = async () => {
        if (busy) return;
        if (!rejectReason.trim()) {
            toast.error("Reason is required");
            return;
        }
        setBusy(true);
        try {
            const r = await api.post(`/admin/prescriptions/${id}/reject`, { reason: rejectReason, notes: rejectNotes || null });
            const cancelled = r.data?.cancelled_orders?.length || 0;
            toast.success(`Rejected${cancelled > 0 ? ` — ${cancelled} order(s) auto-cancelled` : ""}`);
            setRejectOpen(false);
            await goToNextPending();
        } catch (err) {
            toast.error(err?.response?.data?.detail?.message || "Could not reject");
        } finally {
            setBusy(false);
        }
    };

    if (loading || !rx) {
        return <div className="h-[70vh] skeleton rounded-2xl" />;
    }

    const ocr = rx.ocr_data || {};
    const isFinal = rx.verification_status !== "pending_review";

    return (
        <div className="space-y-5" data-testid="admin-rx-detail">
            <div className="flex items-center justify-between">
                <Link to="/admin/prescriptions" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-brand-700">
                    <ArrowLeft className="h-4 w-4" /> All prescriptions
                </Link>
                <span className={`rounded-full px-3 py-1 text-[10px] font-semibold uppercase ring-1 ${
                    rx.verification_status === "approved" ? "bg-emerald-100 text-emerald-700 ring-emerald-200" :
                    rx.verification_status === "rejected" ? "bg-rose-100 text-rose-600 ring-rose-200" :
                    "bg-amber-100 text-amber-700 ring-amber-200"
                }`} data-testid="rx-detail-status">
                    {rx.verification_status.replace("_", " ")}
                </span>
            </div>

            {/* Split screen */}
            <div className="grid gap-5 lg:grid-cols-[1.2fr_1fr] xl:grid-cols-[1.4fr_1fr]">
                {/* LEFT: image */}
                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-900 shadow-soft" data-testid="rx-image-pane">
                    <div className="flex items-center justify-between bg-slate-800 px-4 py-2 text-xs text-slate-200">
                        <span>Prescription image</span>
                        <div className="flex items-center gap-1">
                            <button onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))} className="rounded p-1 hover:bg-slate-700" aria-label="Zoom out" data-testid="rx-zoom-out">
                                <ZoomOut className="h-3.5 w-3.5" />
                            </button>
                            <span className="min-w-[3rem] text-center tabular-nums">{Math.round(zoom * 100)}%</span>
                            <button onClick={() => setZoom((z) => Math.min(3, z + 0.25))} className="rounded p-1 hover:bg-slate-700" aria-label="Zoom in" data-testid="rx-zoom-in">
                                <ZoomIn className="h-3.5 w-3.5" />
                            </button>
                            <a
                                href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/prescriptions/${rx.id}/image`}
                                target="_blank"
                                rel="noreferrer"
                                className="ml-2 rounded p-1 hover:bg-slate-700"
                                aria-label="Open in new tab"
                                data-testid="rx-fullscreen"
                            >
                                <Maximize2 className="h-3.5 w-3.5" />
                            </a>
                        </div>
                    </div>
                    <div className="max-h-[70vh] overflow-auto bg-slate-100 p-4">
                        <img
                            src={`${process.env.REACT_APP_BACKEND_URL || ""}/api/prescriptions/${rx.id}/image`}
                            alt="Prescription"
                            style={{ transform: `scale(${zoom})`, transformOrigin: "top left" }}
                            className="block transition-transform"
                            data-testid="rx-image"
                        />
                    </div>
                </div>

                {/* RIGHT: tabbed panel */}
                <div className="flex flex-col rounded-2xl border border-slate-200 bg-white shadow-soft" data-testid="rx-info-pane">
                    {/* Tabs */}
                    <div className="flex border-b border-slate-200">
                        {TABS.map((t) => (
                            <button
                                key={t.value}
                                onClick={() => setTab(t.value)}
                                className={`flex-1 px-3 py-3 text-xs font-medium transition-colors ${
                                    tab === t.value ? "border-b-2 border-brand-600 text-brand-700" : "text-slate-500 hover:text-slate-900"
                                }`}
                                data-testid={`rx-tab-${t.value}`}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>
                    <div className="flex-1 overflow-y-auto p-5" style={{ maxHeight: "60vh" }}>
                        {tab === "ocr" && (
                            <div data-testid="rx-tab-content-ocr">
                                <InfoRow icon={Stethoscope} label="Doctor" value={ocr.doctor_name} />
                                <InfoRow icon={User2} label="Registration #" value={ocr.doctor_registration_no} />
                                <InfoRow label="Clinic" value={ocr.clinic_name} />
                                <hr className="my-3 border-slate-100" />
                                <InfoRow icon={User2} label="Patient" value={ocr.patient_name} />
                                <InfoRow label="Age" value={ocr.patient_age} />
                                <InfoRow label="Gender" value={ocr.patient_gender} />
                                <InfoRow icon={Calendar} label="Prescription date" value={ocr.prescription_date} />
                                <hr className="my-3 border-slate-100" />
                                <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Medicines ({(ocr.medicines || []).length})</p>
                                {(ocr.medicines || []).length === 0 ? (
                                    <p className="text-xs text-slate-400">No medicines extracted.</p>
                                ) : (
                                    <ul className="space-y-2">
                                        {ocr.medicines.map((m, i) => (
                                            <li key={i} className="rounded-lg border border-slate-100 p-2.5">
                                                <p className="flex items-center gap-2 text-sm font-medium text-slate-900"><Pill className="h-3.5 w-3.5 text-brand-700" /> {m.name}</p>
                                                <div className="mt-1 grid grid-cols-2 gap-1 text-xs text-slate-600">
                                                    {m.dosage && <p>Dosage: {m.dosage}</p>}
                                                    {m.frequency && <p>Frequency: {m.frequency}</p>}
                                                    {m.duration && <p>Duration: {m.duration}</p>}
                                                    {m.quantity && <p>Qty: {m.quantity}</p>}
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                {ocr.notes && (
                                    <>
                                        <hr className="my-3 border-slate-100" />
                                        <InfoRow icon={FileText} label="Doctor's notes" value={ocr.notes} />
                                    </>
                                )}
                                {ocr.confidence_score != null && (
                                    <div className="mt-4 flex items-center gap-2 rounded-lg bg-slate-50 p-2.5 text-xs text-slate-600">
                                        Confidence: <strong className="text-slate-900">{Math.round(ocr.confidence_score * 100)}%</strong>
                                        {ocr.confidence_score < 0.6 && <span className="ml-auto inline-flex items-center gap-1 text-amber-700"><AlertTriangle className="h-3 w-3" /> Low</span>}
                                    </div>
                                )}
                            </div>
                        )}
                        {tab === "copilot" && (
                            <div data-testid="rx-tab-content-copilot" className="space-y-4">
                                <div className="flex flex-wrap items-center gap-2">
                                    <div className="flex items-center gap-1.5 text-brand-700">
                                        <Sparkles className="h-4 w-4" />
                                        <p className="text-xs font-semibold uppercase tracking-wider">AI Pharmacist Co-pilot</p>
                                    </div>
                                    <span className="rounded-full bg-brand-50 px-2 py-0.5 text-[10px] font-medium text-brand-700">Gemini 2.5 Flash</span>
                                    {copilot && (
                                        <button
                                            onClick={() => runCopilot(true)}
                                            disabled={copilotLoading}
                                            className="ml-auto inline-flex items-center gap-1 rounded-md border border-zinc-200 px-2.5 py-1 text-[11px] font-medium text-zinc-600 hover:bg-zinc-50 disabled:opacity-50"
                                            data-testid="copilot-rerun-btn"
                                        >
                                            <RefreshCw className={`h-3 w-3 ${copilotLoading ? "animate-spin" : ""}`} /> Re-run
                                        </button>
                                    )}
                                </div>

                                {!copilot && !copilotLoading && (
                                    <div className="rounded-xl border border-dashed border-brand-200 bg-brand-50/30 p-5 text-center">
                                        <Sparkles className="mx-auto mb-2 h-6 w-6 text-brand-600" />
                                        <p className="text-sm font-medium text-zinc-800">Run AI clinical analysis</p>
                                        <p className="mx-auto mt-1 max-w-md text-xs text-zinc-500">
                                            Gemini will scan this Rx for drug-drug interactions, dosage concerns and contraindications. Use this as a co-pilot — final approval is yours.
                                        </p>
                                        <button
                                            onClick={() => runCopilot(false)}
                                            disabled={copilotLoading}
                                            className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-brand-700 px-3 py-2 text-sm font-medium text-white hover:bg-brand-800 disabled:opacity-50"
                                            data-testid="copilot-run-btn"
                                        >
                                            <Sparkles className="h-3.5 w-3.5" /> Run AI co-pilot
                                        </button>
                                    </div>
                                )}

                                {copilotLoading && (
                                    <div className="rounded-xl border border-brand-100 bg-brand-50/40 p-4 text-sm text-brand-800" data-testid="copilot-loading">
                                        <p className="flex items-center gap-2"><RefreshCw className="h-4 w-4 animate-spin" /> Analyzing prescription… (up to ~20s)</p>
                                    </div>
                                )}

                                {copilot && (
                                    <div className="space-y-3" data-testid="copilot-result">
                                        <div className={`rounded-xl px-3 py-2 ring-1 ${ACTION_TONE[copilot.auto_suggested_action] || ACTION_TONE.review}`}>
                                            <p className="flex items-center gap-2 text-xs font-semibold uppercase">
                                                <ShieldAlert className="h-3.5 w-3.5" /> Suggested action: {copilot.auto_suggested_action}
                                                <span className="ml-auto text-[10px] font-normal opacity-80">conf {(copilot.confidence * 100).toFixed(0)}%</span>
                                            </p>
                                            {copilot.overall_summary && <p className="mt-1.5 text-sm">{copilot.overall_summary}</p>}
                                        </div>

                                        {(copilot.interactions || []).length > 0 && (
                                            <div data-testid="copilot-interactions">
                                                <p className="label-uppercase mb-1.5">Interactions ({copilot.interactions.length})</p>
                                                <ul className="space-y-1.5">
                                                    {copilot.interactions.map((i, idx) => (
                                                        <li key={idx} className={`rounded-lg border p-2.5 text-xs ${SEVERITY_TONE[i.severity] || SEVERITY_TONE.low}`}>
                                                            <p className="font-medium">{i.medicine_a} ↔ {i.medicine_b} <span className="ml-1 inline-block rounded-full bg-white/40 px-1.5 text-[10px] uppercase">{i.severity}</span></p>
                                                            <p className="mt-1 opacity-90">{i.description}</p>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}

                                        {(copilot.dosage_alerts || []).length > 0 && (
                                            <div data-testid="copilot-dosage-alerts">
                                                <p className="label-uppercase mb-1.5">Dosage alerts ({copilot.dosage_alerts.length})</p>
                                                <ul className="space-y-1.5">
                                                    {copilot.dosage_alerts.map((a, idx) => (
                                                        <li key={idx} className="rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-900">
                                                            <p className="font-medium">{a.medicine} <span className="ml-1 inline-block rounded-full bg-white/50 px-1.5 text-[10px] uppercase">{a.alert_type.replace("_", " ")}</span></p>
                                                            <p className="mt-1 opacity-90">{a.description}</p>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}

                                        {(copilot.contraindications || []).length > 0 && (
                                            <div data-testid="copilot-contraindications">
                                                <p className="label-uppercase mb-1.5">Contraindications ({copilot.contraindications.length})</p>
                                                <ul className="space-y-1.5">
                                                    {copilot.contraindications.map((c, idx) => (
                                                        <li key={idx} className="rounded-lg border border-rose-200 bg-rose-50 p-2.5 text-xs text-rose-900">
                                                            <p className="font-medium">{c.medicine} — {c.condition}</p>
                                                            <p className="mt-1 opacity-90">{c.description}</p>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}

                                        {copilot.interactions?.length === 0 && copilot.dosage_alerts?.length === 0 && copilot.contraindications?.length === 0 && (
                                            <p className="rounded-lg border border-emerald-100 bg-emerald-50 p-3 text-xs text-emerald-700" data-testid="copilot-no-concerns">
                                                ✓ No clinical concerns flagged by AI.
                                            </p>
                                        )}

                                        <p className="text-[10px] text-zinc-400">
                                            AI for assistance only — final clinical decision rests with the pharmacist.
                                            {copilot.elapsed_seconds != null && ` • ${copilot.elapsed_seconds}s • ${copilot.model || "gemini"}`}
                                            {copilot.cached && " • cached"}
                                            {copilot.stale && " • cache stale"}
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                        {tab === "customer" && rx.user && (
                            <div data-testid="rx-tab-content-customer">
                                <InfoRow icon={User2} label="Name" value={rx.user.name} />
                                <InfoRow icon={Mail} label="Email" value={rx.user.email} />
                                <InfoRow icon={Phone} label="Phone" value={rx.user.phone} />
                                <InfoRow icon={Calendar} label="Customer since" value={rx.user.created_at ? new Date(rx.user.created_at).toLocaleDateString("en-IN") : "—"} />
                            </div>
                        )}
                        {tab === "linked" && (
                            <div data-testid="rx-tab-content-linked">
                                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Items currently in cart</p>
                                {(rx.linked_cart_items || []).length === 0 ? (
                                    <p className="mt-2 text-xs text-slate-400">No cart items linked to this prescription.</p>
                                ) : (
                                    <ul className="mt-2 space-y-1.5">
                                        {rx.linked_cart_items.map((i, idx) => (
                                            <li key={idx} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2 text-sm">
                                                <span>{i.name} <span className="text-slate-400">× {i.qty}</span></span>
                                                <span className="font-medium">₹{i.selling_price}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                <hr className="my-4 border-slate-100" />
                                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Pending orders</p>
                                {(rx.linked_orders || []).length === 0 ? (
                                    <p className="mt-2 text-xs text-slate-400">No pending orders depend on this prescription.</p>
                                ) : (
                                    <ul className="mt-2 space-y-1.5">
                                        {rx.linked_orders.map((o) => (
                                            <li key={o.id}>
                                                <Link to={`/admin/orders/${o.id}`} className="flex items-center justify-between rounded-lg border border-amber-100 bg-amber-50/50 px-3 py-2 text-sm hover:border-amber-300">
                                                    <span>{o.order_no} <span className="text-slate-400">· {o.status}</span></span>
                                                    <span className="font-medium">₹{o.amounts?.total || 0}</span>
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                {rx.rejection_reason && (
                                    <>
                                        <hr className="my-4 border-slate-100" />
                                        <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700" data-testid="rx-rejection-reason">
                                            <p className="font-semibold uppercase tracking-wider text-[10px]">Rejection reason</p>
                                            <p className="mt-1 text-rose-900">{rx.rejection_reason}</p>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Action bar */}
                    {!isFinal && (
                        <div className="border-t border-slate-200 bg-slate-50/60 p-4">
                            <textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder="Internal notes (optional)..."
                                rows={2}
                                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
                                data-testid="rx-notes-input"
                            />
                            <div className="mt-3 flex gap-2">
                                <button
                                    onClick={() => setRejectOpen(true)}
                                    disabled={busy}
                                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg border border-rose-200 bg-white px-4 py-2.5 text-sm font-medium text-rose-700 transition-colors hover:bg-rose-50 disabled:opacity-50"
                                    data-testid="rx-reject-open"
                                >
                                    <X className="h-4 w-4" /> Reject
                                </button>
                                <button
                                    onClick={approve}
                                    disabled={busy}
                                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-emerald-500 disabled:opacity-50"
                                    data-testid="rx-approve-btn"
                                >
                                    <Check className="h-4 w-4" /> Approve
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Reject dialog */}
            {rejectOpen && (
                <div className="fixed inset-0 z-[70] grid place-items-center bg-slate-900/40 p-4 backdrop-blur-sm" data-testid="rx-reject-modal">
                    <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl">
                        <p className="label-uppercase text-rose-600">Reject prescription</p>
                        <h2 className="mt-1 text-lg font-semibold tracking-tight text-slate-900">Choose a reason</h2>
                        <p className="mt-1 text-xs text-slate-500">The customer will be notified. Any pending order tied to this prescription will be auto-cancelled and refunded.</p>

                        <div className="mt-5 space-y-2">
                            {REJECTION_REASONS.map((r) => (
                                <label key={r.code} className={`flex cursor-pointer items-start gap-2.5 rounded-lg border p-3 text-sm transition-colors ${
                                    rejectReason === r.value ? "border-rose-300 bg-rose-50" : "border-slate-200 hover:border-slate-300"
                                }`}>
                                    <input
                                        type="radio"
                                        name="reject-reason"
                                        value={r.value}
                                        checked={rejectReason === r.value}
                                        onChange={(e) => setRejectReason(e.target.value)}
                                        className="mt-0.5 accent-rose-600"
                                        data-testid={`rx-reject-reason-${r.code}`}
                                    />
                                    <span>{r.value}</span>
                                </label>
                            ))}
                            <textarea
                                value={rejectNotes}
                                onChange={(e) => setRejectNotes(e.target.value)}
                                placeholder="Additional notes (optional)..."
                                rows={2}
                                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-rose-500 focus:outline-none"
                                data-testid="rx-reject-notes"
                            />
                        </div>

                        <div className="mt-6 flex gap-2">
                            <button
                                onClick={() => setRejectOpen(false)}
                                disabled={busy}
                                className="flex-1 rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={reject}
                                disabled={busy}
                                className="flex-1 rounded-lg bg-rose-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-rose-500 disabled:opacity-50"
                                data-testid="rx-reject-confirm"
                            >
                                {busy ? "Rejecting..." : "Confirm reject"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const AdminPrescriptionDetail = () => (
    <AdminAuthGuard>
        <AdminLayout title="Prescription review" subtitle="Verify the prescription against the OCR data">
            <Content />
        </AdminLayout>
    </AdminAuthGuard>
);

export default AdminPrescriptionDetail;
